import { useTranslation } from "react-i18next";
import type { Tournament } from "@/campaigns/modules/tournament/types";
import { SectionLabel } from "@/components/campaigns/campaign-shell/campaign-sections";
import { CampaignJoinState } from "@/components/campaign-card/campaign-join-cta";
import { PrizeText } from "@/components/prize-text";
import { useTournamentLeaderboard } from "@/hooks/tournaments/use-tournament-leaderboard";
import type { PrizeSchema } from "@/types";
import { formatPrizeTitle } from "@/utils/helpers";

/** The next rung up the prize ladder from `position`: the closest tier that
 *  ends above it — or, off the board, the tier that first enters the prizes. */
function nextPrizeTier(
  prizes: PrizeSchema[],
  position: number | undefined
): { reach: number; title: string } | undefined {
  const tiers = prizes
    .map((prize) => ({
      reach: prize.place_to ?? prize.place_from,
      title: prize.title as string | undefined,
    }))
    .filter(
      (tier): tier is { reach: number; title: string } =>
        tier.reach != null && Boolean(tier.title)
    );
  const above =
    position == null ? tiers : tiers.filter((tier) => tier.reach < position);
  return above.reduce<{ reach: number; title: string } | undefined>(
    (best, tier) => (!best || tier.reach > best.reach ? tier : best),
    undefined
  );
}

/**
 * What the player plays for next — "Reach position 5 for" over the prize —
 * and where they stand on joining: a "Joined" pill, or the opt-in button. The
 * prize is priority content, so the caption sits above it as an overline and
 * the prize takes the full width, gliding on one line if it still doesn't fit
 * (never truncated, never broken onto a second row).
 */
export const TournamentNextPrize = ({
  tournament,
  joined,
}: {
  tournament: Tournament;
  joined: boolean;
}) => {
  const { t } = useTranslation();
  const { playerData } = useTournamentLeaderboard(
    String(tournament.campaign.id)
  );
  const next = nextPrizeTier(tournament.prizes, playerData?.position);

  return (
    <div className="flex w-full flex-col gap-3 rounded-(--border-radius) bg-background-card p-3">
      {next && (
        <div className="flex flex-col gap-1">
          <SectionLabel>
            {t("prizes.reachPositionForPrize", "Reach position {{range}} for", {
              range: next.reach,
            })}
          </SectionLabel>
          <PrizeText
            size="inherit"
            weight="bold"
            icon
            fit="marquee"
            className="text-lg"
          >
            {formatPrizeTitle(next.title, t)}
          </PrizeText>
        </div>
      )}
      <CampaignJoinState campaign={tournament.campaign} joined={joined} />
    </div>
  );
};
